'use client';

import { useState } from 'react';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';

import { SkillChatChip } from '@/components/command-skills/skill-chat-chip';
import { collapseSkillDisplay } from '@/components/command-skills/skill-chat';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Response } from '@/components/ui/shadcn-io/ai/response';
import type { ContentSegment, ParsedTool } from '@/components/aios-command/chat/message-parser';
import type { SubagentRun } from '@/utils/command-subagent-nesting';
import { readDelegationPanelNameFromToolInput } from '@/utils/command-subagent-nesting';
import { cn } from '@/lib/utils';

export function formatSubagentDisplayName(name: string): string {
    const spaced = name.replace(/_/g, ' ').replace(/-/g, ' ').trim();
    if (!spaced) return 'Subagent';
    return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function isTaskDelegationTool(tool: ParsedTool): boolean {
    const rawName = tool.type.replace(/^tool[_-]?/i, '');
    if (/^task$/i.test(rawName)) return true;
    if (readDelegationPanelNameFromToolInput(tool.input as Record<string, unknown>)) return true;
    return (tool.subagentRuns?.length ?? 0) > 0;
}

function readTaskInstruction(input: unknown): string | null {
    if (!input || typeof input !== 'object') return null;
    const record = input as Record<string, unknown>;
    const raw =
        (typeof record.description === 'string' && record.description) ||
        (typeof record.prompt === 'string' && record.prompt) ||
        null;
    if (!raw?.trim()) return null;
    return raw.replace(/\s+/g, ' ').trim();
}

function formatNestedToolLabel(type: string): string {
    const raw = type.replace(/^tool[_-]?/i, '').replace(/[_-]+/g, ' ').trim();
    if (!raw) return 'Tool';
    return raw.replace(/\b\w/g, (char) => char.toUpperCase());
}

type StepStatus = 'complete' | 'active' | 'pending';

function toolStepStatus(tool: ParsedTool, isStreaming?: boolean): StepStatus {
    if (tool.state === 'output-error') return 'complete';
    if (
        isStreaming &&
        (tool.state === 'input-streaming' || tool.state === 'input-available')
    ) {
        return tool.output ? 'complete' : 'active';
    }
    if (tool.state === 'output-available' || tool.output) return 'complete';
    return 'pending';
}

function ToolPayloadSection({ label, output }: { label: string; output: string }) {
    const trimmed = output.length > 3200 ? `${output.slice(0, 3200)}…` : output;
    return (
        <div className="overflow-hidden rounded-md border border-border/60 bg-muted/40">
            <div className="border-b border-border/60 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
            </div>
            <pre className="max-h-40 overflow-auto p-2.5 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words text-foreground/85">
                {trimmed}
            </pre>
        </div>
    );
}

interface NestedToolPayloadProps {
    tool: ParsedTool;
}

function NestedToolPayload({ tool }: NestedToolPayloadProps) {
    const hasInput = tool.input && Object.keys(tool.input).length > 0;
    const output = tool.output != null ? String(tool.output) : '';

    return (
        <div className="ml-[18px] mt-1 space-y-2">
            {hasInput ? (
                <ToolPayloadSection
                    label="Request"
                    output={JSON.stringify(tool.input, null, 2)}
                />
            ) : null}
            {output ? <ToolPayloadSection label="Response" output={output} /> : null}
            {tool.errorText ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
                    {tool.errorText}
                </div>
            ) : null}
        </div>
    );
}

interface AgentDelegationPanelProps {
    agentName: string;
    /** Full task instruction shown when expanded */
    instruction?: string | null;
    error?: string | null;
    toolCount: number;
    isRunning: boolean;
    isFailed: boolean;
    isStreaming?: boolean;
    segments: ContentSegment[];
    taskOutput?: string;
}

function AgentDelegationPanel({
    agentName,
    instruction,
    error,
    toolCount,
    isRunning,
    isFailed,
    isStreaming,
    segments,
    taskOutput,
}: AgentDelegationPanelProps) {
    const isWorking = isRunning && Boolean(isStreaming);
    const [open, setOpen] = useState(false);
    const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});

    const flatSteps = segments.filter(
        (segment) =>
            (segment.type === 'tool' && segment.tool) ||
            (segment.type === 'text' && segment.content),
    );

    const hasBrief = Boolean(taskOutput);

    if (flatSteps.length === 0 && !instruction && !error && !hasBrief && !isRunning) {
        return null;
    }

    const toggleToolPayload = (key: string) => {
        setExpandedTools((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="not-prose my-0.5 w-full"
        >
            <CollapsibleTrigger
                className="group flex w-full items-center gap-1.5 rounded-md py-1 pr-1 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
                <ChevronDownIcon
                    className={cn(
                        'size-3.5 shrink-0 opacity-60 transition-transform duration-200',
                        'group-data-[state=open]:rotate-180',
                    )}
                />
                {isWorking ? (
                    <Loader2Icon className="size-3.5 shrink-0 animate-spin opacity-70" />
                ) : null}
                <span className="min-w-0 truncate font-medium text-foreground/90">{agentName}</span>
                {isWorking ? (
                    <>
                        <span className="shrink-0 opacity-40">·</span>
                        <span className="shrink-0 text-muted-foreground">
                            {open && toolCount > 0
                                ? `${toolCount} ${toolCount === 1 ? 'step' : 'steps'}`
                                : 'Working…'}
                        </span>
                    </>
                ) : toolCount > 0 ? (
                    <>
                        <span className="shrink-0 opacity-40">·</span>
                        <span className="shrink-0 tabular-nums">
                            {toolCount} {toolCount === 1 ? 'step' : 'steps'}
                        </span>
                    </>
                ) : null}
                {isFailed ? (
                    <>
                        <span className="shrink-0 opacity-40">·</span>
                        <span className="shrink-0 text-destructive">Failed</span>
                    </>
                ) : null}
            </CollapsibleTrigger>

            <CollapsibleContent className="ml-[18px] border-l border-border/70 pl-3 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0">
                {instruction ? (
                    <div className="py-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Instruction</p>
                        <p className="mt-1 text-sm leading-relaxed text-foreground/90">{instruction}</p>
                    </div>
                ) : null}

                {error ? (
                    <p className="py-1 text-xs leading-relaxed text-destructive">{error}</p>
                ) : null}

                {flatSteps.length > 0 ? (
                    <ul className="space-y-1 py-1">
                        {collapseSkillDisplay(flatSteps).map((piece) => {
                            if (piece.kind === 'skill') {
                                return (
                                    <li key={piece.key} className="py-0.5">
                                        <SkillChatChip name={piece.name} variant={piece.variant} />
                                    </li>
                                );
                            }
                            const segment = piece.segment;
                            const idx = piece.index;
                            if (segment.type === 'text' && segment.content) {
                                return (
                                    <li key={`delegation-text-${idx}`} className="py-0.5">
                                        <p className="text-xs font-medium text-muted-foreground">
                                            Response
                                        </p>
                                        <Response
                                            className="mt-0.5 break-words text-sm leading-relaxed text-muted-foreground"
                                            parseIncompleteMarkdown={Boolean(isStreaming)}
                                        >
                                            {segment.content}
                                        </Response>
                                    </li>
                                );
                            }

                            if (segment.type === 'tool' && segment.tool) {
                                const tool = segment.tool;
                                const stepStatus = toolStepStatus(tool, isStreaming);
                                const toolKey = tool.toolCallId ?? `delegation-tool-${idx}`;
                                const hasPayload =
                                    Boolean(tool.output) ||
                                    Boolean(tool.errorText) ||
                                    (tool.input && Object.keys(tool.input).length > 0);
                                const payloadOpen = expandedTools[toolKey] ?? false;
                                const label = formatNestedToolLabel(tool.type);

                                return (
                                    <li key={toolKey} className="py-0.5">
                                        {hasPayload ? (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleToolPayload(toolKey);
                                                }}
                                                className="group/tool flex w-full items-center gap-1.5 rounded-md py-0.5 pr-1 text-left text-sm transition-colors hover:text-foreground"
                                            >
                                                <ChevronDownIcon
                                                    className={cn(
                                                        'size-3.5 shrink-0 text-muted-foreground opacity-60 transition-transform duration-200',
                                                        payloadOpen && 'rotate-180',
                                                    )}
                                                />
                                                <span
                                                    className={cn(
                                                        'min-w-0 flex-1 truncate',
                                                        stepStatus === 'active'
                                                            ? 'text-foreground'
                                                            : 'text-muted-foreground group-hover/tool:text-foreground',
                                                    )}
                                                >
                                                    {label}
                                                </span>
                                                {stepStatus === 'active' ? (
                                                    <Loader2Icon className="size-3 shrink-0 animate-spin opacity-50" />
                                                ) : null}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-1.5 py-0.5 text-sm">
                                                <ChevronDownIcon className="size-3.5 shrink-0 opacity-0" aria-hidden />
                                                <span
                                                    className={cn(
                                                        'min-w-0 truncate',
                                                        stepStatus === 'active'
                                                            ? 'text-foreground'
                                                            : 'text-muted-foreground',
                                                    )}
                                                >
                                                    {label}
                                                </span>
                                                {stepStatus === 'active' ? (
                                                    <Loader2Icon className="size-3 shrink-0 animate-spin opacity-50" />
                                                ) : null}
                                            </div>
                                        )}

                                        {payloadOpen && hasPayload ? (
                                            <NestedToolPayload tool={tool} />
                                        ) : null}
                                    </li>
                                );
                            }

                            return null;
                        })}
                    </ul>
                ) : isWorking ? (
                    <p className="py-1 text-xs text-muted-foreground">Working…</p>
                ) : null}

                {taskOutput ? (
                    <div className="py-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Result</p>
                        <Response className="mt-1 break-words text-sm leading-relaxed text-muted-foreground">
                            {taskOutput}
                        </Response>
                    </div>
                ) : null}
            </CollapsibleContent>
        </Collapsible>
    );
}

interface DelegationTaskPanelProps {
    tool: ParsedTool;
    run: SubagentRun;
    isStreaming?: boolean;
}

export function DelegationTaskPanel({ tool, run, isStreaming }: DelegationTaskPanelProps) {
    const toolCount = run.nestedSegments.filter((segment) => segment.type === 'tool').length;
    const subagentName =
        formatSubagentDisplayName(run.name) ||
        formatSubagentDisplayName(
            readDelegationPanelNameFromToolInput(tool.input as Record<string, unknown>) ??
                'subagent',
        );

    return (
        <AgentDelegationPanel
            agentName={subagentName}
            instruction={readTaskInstruction(tool.input)}
            error={run.error}
            toolCount={toolCount}
            isRunning={run.status === 'running'}
            isFailed={run.status === 'failed' || tool.state === 'output-error'}
            isStreaming={isStreaming}
            segments={run.nestedSegments}
            taskOutput={tool.output != null ? String(tool.output) : undefined}
        />
    );
}

interface SubagentWorkPanelProps {
    run: SubagentRun;
    isStreaming?: boolean;
}

export function SubagentWorkPanel({ run, isStreaming }: SubagentWorkPanelProps) {
    const toolCount = run.nestedSegments.filter((segment) => segment.type === 'tool').length;

    return (
        <AgentDelegationPanel
            agentName={formatSubagentDisplayName(run.name)}
            error={run.error}
            toolCount={toolCount}
            isRunning={run.status === 'running'}
            isFailed={run.status === 'failed'}
            isStreaming={isStreaming}
            segments={run.nestedSegments}
        />
    );
}
